import * as React from 'react';

import { FieldValues, UseControllerProps, useWatch } from 'react-hook-form';
import { Autocomplete, FilterOptionsState, TextField } from '@mui/material';

import _startCase from 'lodash/startCase';

import { SyntheticEvent } from 'react';
import { matchSorter } from 'match-sorter';

import { AirportInfo } from '../core/api/clients';
import { useFormUpdateProxy } from '../core/context/FormUpdateProxyContext';
import useEditableController from '../core/api/hooks/common/useEditableController';

import ReadOnlyField from './ReadOnlyField';
import EditableField from './EditableField';

interface ControlledAirportSelectProps<TFieldValues extends FieldValues> extends UseControllerProps<TFieldValues> {
  label?: string,
  readOnly?: boolean,
  options: AirportInfo[],
  disableEditPopper?: boolean,
  isEditable?: boolean,
}

function ControlledAirportSelect<TFieldValues extends FieldValues>({
  control,
  label,
  name,
  readOnly,
  rules,
  options,
  disableEditPopper,
  isEditable = true,
}: ControlledAirportSelectProps<TFieldValues>) {
  const { requestUpdate } = useFormUpdateProxy();
  const {
    field,
    fieldState,
    revertChanges,
    commitChanges,
  } = useEditableController<TFieldValues>({
    name,
    control,
    defaultValue: '' as any,
  });
  const [value, setValue] = React.useState<AirportInfo | null>(null);
  const [inputValue, setInputValue] = React.useState<string>('');

  const icaoId = useWatch({ control, name });

  const isAirportInfo = (o: any): o is AirportInfo => (o as AirportInfo).icaoid !== undefined;

  React.useEffect(() => {
    const airport = options.find((o: AirportInfo) => o.icaoid === icaoId);
    if (airport) {
      setValue(airport);
    }
  }, [icaoId, options]);

  // display airports matched within the icaoid on top, then matches within the name
  const filterOptions = (
    searchOptions : AirportInfo[],
    { inputValue: searchValue } : FilterOptionsState<AirportInfo>,
  ) => matchSorter(searchOptions, searchValue, {
    keys: [
      { threshold: matchSorter.rankings.CONTAINS, key: 'icaoid' },
      { threshold: matchSorter.rankings.CONTAINS, key: 'name' },
    ],
    sorter: (rankedItems) => {
      const customRankedItems = [...rankedItems];

      if (searchValue && searchValue !== '') {
        let indexOffset = 0;
        rankedItems.forEach((item, index) => {
          if (!item.item.icaoid?.includes(searchValue)) {
            customRankedItems.splice(index - indexOffset, 1);
            customRankedItems.push(item);
            indexOffset += 1;
          }
        });
      }
      return customRankedItems;
    },
  });

  return (
    <EditableField
      fieldName={name}
      renderMode={readOnly ? 'read' : 'edit'}
      saveSuccess={() => {
        commitChanges();
        requestUpdate();
      }}
      onEditCancel={revertChanges}
      disableEditPopper={disableEditPopper}
      renderEditField={() => (
        <Autocomplete
          autoComplete
          autoSelect
          options={options}
          filterOptions={filterOptions}
          getOptionLabel={(o: AirportInfo) => `${o.icaoid} - ${o.name}`}
          value={value}
          inputValue={inputValue}
          onChange={(e: SyntheticEvent, v: string | AirportInfo | null) => {
            field.onChange(v !== null && isAirportInfo(v) ? v.icaoid : v);
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue.toUpperCase());
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              required={rules?.required === true ?? false}
              label={`${label || _startCase(name)}`}
              error={fieldState.invalid}
              helperText={fieldState.invalid && fieldState.error ? fieldState.error.message : null}
              onBlur={field.onBlur}
            />
          )}
          disabled={!isEditable}
        />
      )}
      renderReadField={() => (
        <ReadOnlyField
          name={label || _startCase(name)}
          value={value !== null ? `${value.icaoid} - ${value.name}` : ''}
        />
      )}
    />
  );
}

export default ControlledAirportSelect;
